import React from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import ContentWrapper from "../../Layout/ContentWrapper";
import { Panel, Tabs, Tab, Button } from "react-bootstrap-3";
import { getLocaleCountryMap, entityToFormValuesMapper } from "../../Common/utils";
import { validateFields } from "../Common/validation";
import WidgetRenderer from "../Common/WidgetRenderer";
import EntityErrorMessage from "../Common/EntityErrorMessage";
import EntityResultMessage from "../Common/EntityResultMessage";
import { Link } from "react-router-dom";
import { FormattedMessage, defineMessages } from "react-intl";
import styled from "styled-components";
import { updateObjectByPath } from "../utils";
import { SHARING_TYPE, UPDATE_ENTITY_ACTIONS } from "../../Common/constants";
import deepEqual from "deep-equal";
import { withUserAuthInfo } from "../../../../lib/auth";
import Menu from "./Menu";
import media from "../../../../lib/media";

const EmptyFields = styled.div`
  color: #b20e0e !important;
  background-color: #f4dbdb;
  display: block;
  padding: 5px 10px;
  font-size: 14px;
  margin: 4px 0;
  border-radius: 5px;
`;
const ErrorContainer = styled.div`
  color: #b20e0e !important;
  background-color: #f4dbdb;
  display: inline-block;
  padding: 3px 5px;
  margin-top: 4px;
  border-radius: 5px;
`;
const ErrorTabContainer = styled(ErrorContainer)`
  margin-top: 0;
  margin-bottom: 4px;
`;
const ReminderContainer = styled.div`
  margin-top: 30px;
  padding: 5px;
  background-color: #fff4e7;
  color: #ff9125;
  max-width: 300px;
  border-radius: 5px;
  font-weight: 600;
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
`;

const StyledContentWrapper = styled.div`
  .form-control:not(textarea) {
    max-width: 300px;
  }
`;

const LinkStyled = styled.a`
  cursor: pointer;
`;

const BackToTopButton = styled.div`
  display: ${props => (props.showBackToTopButton ? `flex` : `none`)};
  cursor: pointer;
  background-color: #23b7e5;
  width: 66px;
  height: 66px;
  border-radius: 100px;
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 2;
  align-items: center;
  justify-content: center;
  & i {
    margin-top: 5px;
    width: 20px;
    height: 20px;
    border: solid white;
    border-width: 0 4px 4px 0;
    display: inline-block;
    padding: 3px;
    transform: rotate(-135deg);
  }
`;
const messages = defineMessages({
  submit: {
    id: "app.entityForm.submit",
    description: "Submit new entity",
    defaultMessage: "Submit",
  },
  search: {
    id: "app.entityForm.search",
    description: "Search",
    defaultMessage: "Search",
  },
  goBack: {
    id: "app.entityForm.goBack",
    description: "Go back to previous screen",
    defaultMessage: "Go back",
  },
  remember: {
    id: "app.entityForm.remember",
    description: "remember Text",
    defaultMessage: "Remember to save your changes",
  },
  fieldErrors: {
    id: "app.form.someFieldsErrors",
    description: "error fields text",
    defaultMessage: "Some fields have errors.",
  },
  tabsErrors: {
    id: "app.form.someTabsErrors",
    description: "error tabs text",
    defaultMessage: "Some tabs have errors.",
  },
  blankErrors: {
    id: "app.form.emptyFields",
    description: "empty fields text",
    defaultMessage: "Check the fields in red",
  },
  seeAdvancedOptions: {
    id: "app.entityForm.seeAdvancedOptions",
    description: "See advanced options",
    defaultMessage: "See advanced options",
  },
  hideAdvancedOptions: {
    id: "app.entityForm.hideAdvancedOptions",
    description: "Hide advanced options",
    defaultMessage: "Hide advanced options",
  },
  readOnly: {
    id: "app.entityForm.readOnly",
    description: "Read only",
    defaultMessage: "Read only",
  },
  clickHere: {
    id: "app.entityForm.clickHere",
    description: "click here",
    defaultMessage: "click here",
  },
});

require("flag-icon-css/css/flag-icon.css");

function getTranslatedFieldError({ fieldName, locale, formErrors }) {
  return (
    formErrors.translations &&
    formErrors.translations[locale] &&
    formErrors.translations[locale][fieldName]
  );
}

function Field({
  field,
  entity,
  entities,
  validation,
  renderers,
  errors,
  value,
  hasErrors,
  onChange,
  formValues,
  tickers,
  showAdvancedTranslations,
  ...props
}) {
  const fieldName = field.fieldName;
  const fieldType = validation && validation.type;
  if (
    renderers &&
    renderers.hasOwnProperty(fieldName) &&
    typeof renderers[fieldName] === "function"
  ) {
    return renderers[fieldName]({ field, entity, entities, fieldType });
  }
  return (
    <WidgetRenderer
      field={field}
      entity={entity}
      entities={entities}
      validation={validation}
      rendererType={field.widget || null}
      fieldType={fieldType}
      errors={errors}
      value={value}
      hasErrors={hasErrors}
      onChange={onChange}
      formValues={formValues}
      tickers={tickers}
      showAdvancedTranslations={showAdvancedTranslations}
      caller={"form"}
      {...props}
    />
  );
}

const StyledTabs = styled(Tabs)`
  .tab-content {
    padding: 0;
  }

  .panel {
    margin-bottom: 0;
    border: none;
  }
`;

const StyledTranslationTabs = styled(Tabs)`
  .tab-content {
    padding: 10px 20px;
  }

  .panel {
    margin-bottom: 21px;
    border: 1px solid transparent;
  }

  .flag-icon-es-eu {
    background-image: url("https://s3.eu-central-1.amazonaws.com/witbooking.static/img/ikurrina.png");
    background-size: 28px;
  }
`;

export const StyledSpinner = styled.i`
  .fa-spin-custom,
  .glyphicon-spin {
    animation: spin 1000ms infinite linear;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }
`;

export const StyledSpinnerButton = styled(Button)`
  min-width: 78px;
`;

const MenuContainer = styled.div`
  ${props =>
    props.showMenu
      ? `display: flex;   & > *:last-child {
  flex: auto;
  }`
      : ``};
`;

const StyledMenu = styled(Menu)`
  z-index: 2;
  display: none;
  margin-right: 15px;
  ${media.up.sml`
    display: block;
  `};
`;

class EntityForm extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.handleSelect = this.handleSelect.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.renderTranslationTab = this.renderTranslationTab.bind(this);
    this.localeCountryMap = getLocaleCountryMap({
      locales: this.props.locales,
    });

    this.state = {
      formValues: this.mapToFormValues({
        entity: this.props.entity,
        fields: this.props.fields,
        locales: this.props.locales,
        translationConfig: this.props.translation,
        locale: this.props.locale,
        showAdvancedTranslations: true,
      }),
      formErrors: {},
      showEmptyFieldsError: true,
      translated: this.props.translation && this.props.translation.translated,
      dirty: false,
      showAdvancedTranslations: false,
      showBackToTopButton: false,
      customValidateError: "",
    };
  }

  handleScroll = () => {
    if (window.window.pageYOffset > 150 && !this.state.showBackToTopButton) {
      this.setState({ showBackToTopButton: true });
    }

    if (window.window.pageYOffset < 150 && this.state.showBackToTopButton) {
      this.setState({ showBackToTopButton: false });
    }
  };

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.entity && nextProps.entity && !deepEqual(this.props.entity, nextProps.entity)) {
      this.setState({
        formValues: this.mapToFormValues({
          entity: nextProps.entity,
          fields: nextProps.fields,
          locales: nextProps.locales,
          translationConfig: nextProps.translation,
          locale: this.props.locale,
        }),
      });
    }
  }

  mapToFormValues = ({ entity, fields, locales, translationConfig, locale }) => {
    let entityFormValuesMapped = this.props.entityToFormValuesMapper({
      entity,
      fields,
      locales,
      translationConfig,
      locale,
    });
    fields &&
      fields.forEach(element => {
        if (element.nested) {
          entityFormValuesMapped[element.fieldName] &&
            entityFormValuesMapped[element.fieldName].forEach((value, index) => {
              value._tempId = index + 1;
            });
        }
      });
    if (!!entity._tempId) {
      entityFormValuesMapped._tempId = entity._tempId;
    }
    return entityFormValuesMapped;
  };

  handleOnChange({ event, value: givenValue, field, idx, translation }) {
    const relatedFields =
      (this.props.validation[field] && this.props.validation[field].relatedFields) || [];
    let value = givenValue !== undefined ? givenValue : event && event.target && event.target.value;

    if (event && event.target && event.target.type === "checkbox") {
      value = (event.target.checked && true) || false;
    }

    let newFormValues = {};
    const newErrors = { ...this.state.formErrors };

    let formValues = this.state.formValues;

    if (!translation) {
      delete newErrors[field];
      relatedFields.forEach(relatedFieldName => {
        delete newErrors[relatedFieldName];
      });

      const splitField = field && field.split(".");
      if (splitField && formValues[splitField[0]] && formValues[splitField[0]][splitField[1]]) {
        updateObjectByPath({
          action: UPDATE_ENTITY_ACTIONS.REPLACE,
          object: formValues,
          path: field,
          value,
        });

        newFormValues = { ...formValues };
      } else {
        newFormValues = { ...formValues, [field]: value };
      }
    }
    let locale = null;
    if (translation && this.props.translation && this.props.translation.fieldName) {
      const translationField = this.props.translation.fieldName;
      const newTranslations = [].concat(formValues[translationField]);
      newTranslations[idx][field] = value;
      newFormValues = Object.assign({}, formValues, {
        [translationField]: newTranslations,
      });
      locale = newTranslations[idx].locale;
    }
    const errors = validateFields({
      fields: this.props.fields.filter(
        it => it.fieldName === field || relatedFields.includes(it.fieldName)
      ),
      validationConfig: this.props.validation,
      formValues: newFormValues,
      translationConfig: this.props.translation,
    });

    let newState;

    if (locale) {
      const translationField = this.props.translation.fieldName;
      //Clean-up previous field errors
      if (
        newErrors[translationField] &&
        newErrors[translationField][locale] &&
        newErrors[translationField][locale][field]
      ) {
        delete newErrors[translationField][locale][field];
      }
      if (
        errors[translationField] &&
        errors[translationField][locale] &&
        errors[translationField][locale][field] &&
        errors[translationField][locale][field].length > 0
      ) {
        if (!newErrors[translationField]) {
          newErrors[translationField] = {};
        }
        if (!newErrors[translationField][locale]) {
          newErrors[translationField][locale] = {};
        }

        newErrors[translationField][locale][field] = errors[translationField][locale][field];
      }
      newState = { formValues: newFormValues, formErrors: newErrors };
    } else {
      newState = {
        formValues: newFormValues,
        formErrors: { ...newErrors, ...errors },
      };
    }
    if (
      // As it's not something critical, JSON stringify for better performance?
      deepEqual(this.state.formErrors, newState.formErrors)
    ) {
      newState = { ...newState, showEmptyFieldsError: false };
    }
    if (this.state.dirty !== true) {
      newState = {
        ...newState,
        dirty: true,
      };
    }

    if (this.props.afterOnChange) {
      this.props.afterOnChange({
        event,
        value,
        field,
        idx,
        translation,
        newState,
      });
    }

    if (this.props.beforeFormStateSetHook) {
      newState = this.props.beforeFormStateSetHook({
        event,
        value: givenValue,
        field,
        idx,
        translation,
        newState,
      });
    }

    this.setState(newState);
  }

  updateFormValue = ({ action, formValueProperties, value }) => {
    let formValues = this.state.formValues;

    updateObjectByPath({
      action,
      object: formValues,
      path: formValueProperties,
      value,
    });

    this.setState({ formValues });
  };

  handleSelect(key) {
    this.setState({ localesTabKey: key });
  }

  handleSubmit(event, fields, customValidate) {
    event.preventDefault();
    event.stopPropagation();
    let customError = "";

    if (typeof customValidate === "function") {
      customError = customValidate(this.state.formValues);
    }
    //return errors instead of bool, save to state, render if exist
    const errors = validateFields({
      fields: fields,
      validationConfig: this.props.validation,
      formValues: this.state.formValues,
      translationConfig: this.props.translation,
    });
    if (Object.keys(errors).length > 0) {
      this.setState({
        formErrors: errors,
        dirty: false,
        showEmptyFieldsError: true,
        customValidateError: customError,
      });
    } else {
      this.setState({ formErrors: errors, dirty: false, customValidateError: customError });
    }
    if (errors && Object.keys(errors).length > 0) {
      console.log("there are form errors: ");
      console.log(errors);
    }
    fields.forEach(element => {
      if (element.nested) {
        this.state.formValues[element.fieldName].forEach(value => {
          if (value._tempId) {
            delete value._tempId;
          }
        });
      }
    });
    if (!customError && (!errors || Object.keys(errors).length === 0)) {
      this.props.onSubmit &&
        this.props.onSubmit({
          formValues: this.state.formValues,
          entity: this.props.entity,
        });
      if (this.props.afterSubmit && typeof this.props.afterSubmit === "function") {
        this.props.afterSubmit(this.props);
      }
      /** this function is used to delete the message after 5 seconds */
      setTimeout(this.props.resetResult, 5000);
    }
  }

  handleAdvancedTranslations = () =>
    this.setState({
      showAdvancedTranslations: !this.state.showAdvancedTranslations,
    });

  renderTranslationTab({
    key,
    translation,
    idx,
    handleOnChange,
    formErrors,
    translatedFields,
    entity,
    entities,
    validation,
    renderers,
    formValues,
    tickers,
    relatedEntities,
    hasAdvancedTranslations,
    allowImageEdit,
  }) {
    const { locale } = translation;
    const country = this.localeCountryMap[locale];
    const getError = fieldName => getTranslatedFieldError({ fieldName, locale, formErrors });
    const hasError =
      formErrors.translations &&
      formErrors.translations[locale] &&
      Object.keys(formErrors.translations[locale]).length > 0;
    return (
      <Tab
        key={key}
        eventKey={`locale-${locale}`}
        tabClassName={hasError ? "tab-error" : ""}
        title={<span className={`flag-icon flag-icon-${country}`} />}
        mountOnEnter
        unmountOnExit>
        {translatedFields.map(field => {
          const fieldErrors = getError(field.fieldName) || [];
          return (
            <Field
              field={field}
              entity={entity}
              entities={entities}
              renderers={renderers}
              onChange={({ event, field, ...rest }) =>
                handleOnChange({
                  event,
                  field,
                  idx,
                  translation: true,
                  ...rest,
                })
              }
              validation={validation[field.fieldName]}
              errors={fieldErrors}
              value={translation[field.fieldName]}
              hasErrors={fieldErrors.length > 0}
              key={field.fieldName}
              formValues={formValues}
              tickers={tickers}
              relatedEntities={relatedEntities}
              updateEntity={this.props.updateEntity}
              updateFormValue={this.updateFormValue}
              showAdvancedTranslations={this.state.showAdvancedTranslations}
              allowImageEdit={allowImageEdit}
            />
          );
        })}

        {hasAdvancedTranslations && (
          <LinkStyled onClick={this.handleAdvancedTranslations}>
            {this.state.showAdvancedTranslations ? (
              <FormattedMessage {...messages.hideAdvancedOptions} />
            ) : (
              <FormattedMessage {...messages.seeAdvancedOptions} />
            )}
          </LinkStyled>
        )}
      </Tab>
    );
  }

  handleScrollToTop = () => {
    window.scrollTo(0, 0);
  };

  getFormatChildren = (
    children,
    entity,
    entities,
    renderers,
    formValues,
    handleOnChange,
    validation,
    formErrors,
    hasErrors,
    tickers,
    locales,
    nestedListWidgets,
    relatedEntities,
    nestedFormWidgets,
    showAdvancedTranslations
  ) => {
    return children.map(child => (
      <Field
        field={child}
        entity={entity}
        entities={entities}
        renderers={renderers}
        formValues={formValues}
        onChange={handleOnChange}
        validation={validation[child.fieldName]}
        errors={formErrors[child.fieldName]}
        value={formValues[child.fieldName]}
        hasErrors={hasErrors(child.fieldName)}
        key={child.fieldName}
        tickers={tickers}
        locales={locales}
        ListWidget={nestedListWidgets[child.fieldName]}
        FormWidget={nestedFormWidgets[child.fieldName]}
        relatedEntities={relatedEntities}
        updateEntity={this.props.updateEntity}
        updateFormValue={this.updateFormValue}
        showAdvancedTranslations={showAdvancedTranslations}
      />
    ));
  };

  getAccordionGroupFieldsByParent = (
    fields,
    entity,
    entities,
    renderers,
    formValues,
    handleOnChange,
    validation,
    formErrors,
    hasErrors,
    tickers,
    locales,
    nestedListWidgets,
    relatedEntities,
    nestedFormWidgets,
    showAdvancedTranslations
  ) => {
    const filteredFields = fields.filter(f => f.parent) || [];
    if (filteredFields.length) {
      const parents = _.uniqBy(filteredFields, "parent").map(p => p.parent) || [];
      parents.forEach(parent => {
        const children = filteredFields.filter(f => f.parent === parent) || [];
        const parentIndex = fields.findIndex(p => p.fieldName === parent);
        if (parentIndex >= 0) {
          fields[parentIndex].children = this.getFormatChildren(
            children,
            entity,
            entities,
            renderers,
            formValues,
            handleOnChange,
            validation,
            formErrors,
            hasErrors,
            tickers,
            locales,
            nestedListWidgets,
            relatedEntities,
            nestedFormWidgets,
            showAdvancedTranslations
          );
        }
      });
      return fields.filter(f => !f.parent);
    }
    return fields;
  };

  render() {
    const {
      name,
      entity,
      entities,
      validation,
      renderers,
      tickers,
      locales,
      nestedListWidgets,
      nestedFormWidgets,
      nested,
      entityError,
      entityResult,
      loading,
      getListPath,
      onCancelEdit,
      relatedEntities,
      locale,
      SubmitButton,
      BackButton,
      hideBackButton,
      translation,
      buttonMessage,
      tabs,
      doesntShowSaveReminder,
      userInfo,
      customValidate,
      showMenu,
      allowImageEdit,
    } = this.props;
    const { handleOnChange, handleSubmit, renderTranslationTab } = this;
    const { formValues, formErrors, showAdvancedTranslations } = this.state;
    const hasErrors = fieldName => !!formErrors[fieldName];

    let fields = this.props.fields.filter(
      field =>
        !field.hasOwnProperty("visible") ||
        field.visible({ formValues, entity, showAdvancedTranslations, userInfo })
    );
    fields = this.getAccordionGroupFieldsByParent(
      fields,
      entity,
      entities,
      renderers,
      formValues,
      handleOnChange,
      validation,
      formErrors,
      hasErrors,
      tickers,
      locales,
      nestedListWidgets,
      relatedEntities,
      nestedFormWidgets,
      showAdvancedTranslations
    );
    const standardFields = (fields && fields.filter(field => !field.translated)) || [];
    const translatedFields = (fields && fields.filter(field => field.translated)) || [];
    const hasTranslationsList = Boolean(
      fields && fields.filter(field => field.translationsList).length > 0
    );

    const hasAdvancedTranslations = translation.hasAdvancedTranslations;

    const submitButton = loading ? (
      <StyledSpinnerButton type="submit" bsClass="btn btn-info" disabled>
        <StyledSpinner className="fa fa-spinner fa-spin" />
      </StyledSpinnerButton>
    ) : (
      (SubmitButton && <SubmitButton />) || (
        <Button type="submit" bsClass="btn btn-info">
          <FormattedMessage {...messages[buttonMessage]} />
        </Button>
      )
    );

    const readOnly =
      this.props.readOnly ||
      (entity.sharingType &&
        entity.parentPropertyTicker &&
        entity.sharingType === SHARING_TYPE.SHARED);

    let backButton = nested ? (
      <Button bsClass="btn btn-default mr ml" onClick={event => onCancelEdit({ event })}>
        <FormattedMessage {...messages.goBack} />
      </Button>
    ) : (
      <Link className="btn btn-default mr ml" role="button" to={getListPath({ entity })}>
        <FormattedMessage {...messages.goBack} />
      </Link>
    );

    if (BackButton) {
      backButton = nested ? (
        <BackButton onClick={event => onCancelEdit({ event })} />
      ) : (
        <Link to={getListPath({ entity })}>
          <BackButton />
        </Link>
      );
    }

    const isEmptyTranslatedFields =
      translatedFields &&
      translatedFields.every(translatedField => translatedField.widget === "hidden");

    let chainParentLink = undefined;

    if (
      entity.parentPropertyTicker &&
      entity.sharingType &&
      entity.sharingType === SHARING_TYPE.SHARED
    ) {
      const location = window.location.href;
      const indexPromos = location.indexOf("/promotions");
      const indexNg = location.indexOf("/ng/") + 4;
      const indexId = location.indexOf("/", indexPromos + 1);
      const propertyTicker = location.substring(indexNg, indexPromos);
      const id = location.substring(indexId);
      chainParentLink = location
        .replace(propertyTicker, entity.parentPropertyTicker)
        .replace(id, "");
    }

    return (
      <StyledContentWrapper>
        <ContentWrapper unwrap={nested}>
          {nested ? "" : <div className="content-heading">{name}</div>}
          <MenuContainer showMenu={showMenu}>
            {showMenu && <StyledMenu fields={fields} showMenu={showMenu} locale={locale} />}
            {!tabs ? (
              <Panel>
                <form onSubmit={event => handleSubmit(event, fields, customValidate)}>
                  {standardFields.map(field => {
                    if (
                      field.translationsList &&
                      !isEmptyTranslatedFields &&
                      translatedFields.length > 0
                    ) {
                      return (
                        <fieldset>
                          <legend className="translationTitle">
                            {this.props.translation.displayName}
                          </legend>
                          {formErrors.translations ? (
                            <ul className="parsley-errors-list filled">
                              <li className="parsley-required">
                                <ErrorTabContainer>
                                  <FormattedMessage {...messages.tabsErrors} />
                                </ErrorTabContainer>
                              </li>
                            </ul>
                          ) : (
                            ""
                          )}
                          <StyledTranslationTabs
                            activeKey={this.state.localesTabKey}
                            onSelect={this.handleSelect.bind(this)}
                            id="tabID">
                            {formValues.translations.map((translation, idx) =>
                              renderTranslationTab({
                                key: translation.locale,
                                translation,
                                idx,
                                handleOnChange,
                                formErrors,
                                translatedFields,
                                entity,
                                entities,
                                validation,
                                renderers,
                                formValues,
                                tickers,
                                relatedEntities,
                                hasAdvancedTranslations,
                                allowImageEdit,
                              })
                            )}
                          </StyledTranslationTabs>
                        </fieldset>
                      );
                    } else {
                      return (
                        <Field
                          field={field}
                          entity={entity}
                          entities={entities}
                          renderers={renderers}
                          formValues={formValues}
                          onChange={handleOnChange}
                          validation={validation[field.fieldName]}
                          errors={formErrors[field.fieldName]}
                          value={formValues[field.fieldName]}
                          hasErrors={hasErrors(field.fieldName)}
                          key={field.fieldName}
                          tickers={tickers}
                          relatedEntities={relatedEntities}
                          locale={locale}
                          updateEntity={this.props.updateEntity}
                          updateFormValue={this.updateFormValue}
                          showAdvancedTranslations={showAdvancedTranslations}
                          locales={locales}
                          ListWidget={nestedListWidgets[field.fieldName]}
                          FormWidget={nestedFormWidgets[field.fieldName]}
                          allowImageEdit={allowImageEdit}
                        />
                      );
                    }
                  })}

                  {!hasTranslationsList &&
                  !isEmptyTranslatedFields &&
                  translatedFields.length > 0 ? (
                    <fieldset>
                      <legend className="translationTitle">
                        {this.props.translation.displayName}
                      </legend>
                      {formErrors.translations ? (
                        <ul className="parsley-errors-list filled">
                          <li className="parsley-required">
                            <ErrorTabContainer>
                              <FormattedMessage {...messages.tabsErrors} />
                            </ErrorTabContainer>
                          </li>
                        </ul>
                      ) : (
                        ""
                      )}
                      <StyledTranslationTabs
                        activeKey={this.state.localesTabKey}
                        onSelect={this.handleSelect.bind(this)}
                        id="tabID">
                        {formValues.translations.map((translation, idx) =>
                          renderTranslationTab({
                            key: translation.locale,
                            translation,
                            idx,
                            handleOnChange,
                            formErrors,
                            translatedFields,
                            entity,
                            entities,
                            validation,
                            renderers,
                            formValues,
                            tickers,
                            relatedEntities,
                            hasAdvancedTranslations,
                          })
                        )}
                      </StyledTranslationTabs>
                    </fieldset>
                  ) : (
                    ""
                  )}
                  {!readOnly && this.state.dirty && !nested && !doesntShowSaveReminder && (
                    <ReminderContainer className="nestedSaveReminder">
                      <FormattedMessage {...messages.remember} />
                    </ReminderContainer>
                  )}
                  {entityError && !this.state.dirty && (
                    <EntityErrorMessage entityError={entityError} />
                  )}

                  {entityResult && !this.state.dirty && hasErrors && (
                    <EntityResultMessage entityResult={entityResult} />
                  )}
                  {/* Opt-in slot for content rendered just before the submit button (e.g. the
                      questionnaire inventory-line selector). Other consumers omit it. */}
                  {this.props.beforeSubmit}
                  {this.state.customValidateError && (
                    <EmptyFields>{this.state.customValidateError}</EmptyFields>
                  )}
                  {Object.keys(this.state.formErrors).length >= 1 && this.state.showEmptyFieldsError && (
                    <EmptyFields>
                      <FormattedMessage {...messages.blankErrors} />
                    </EmptyFields>
                  )}
                  {!readOnly && submitButton}
                  {!hideBackButton && backButton}
                </form>
                {this.props.children && this.props.children}
              </Panel>
            ) : (
              <StyledTabs defaultActiveKey={0} id="form-tabs">
                {tabs
                  .map(tab =>
                    entity.id
                      ? { ...tab, title: tab.title.edit || tab.title }
                      : { ...tab, title: tab.title.create || tab.title }
                  )
                  .map((tab, index) => (
                    <Tab eventKey={index} title={tab.title} key={index}>
                      <Panel>
                        <form onSubmit={event => handleSubmit(event, fields, customValidate)}>
                          {chainParentLink && (
                            <ErrorContainer>
                              <FormattedMessage {...messages.readOnly} />{" "}
                              <a href={chainParentLink}>
                                <FormattedMessage {...messages.clickHere} />
                              </a>
                            </ErrorContainer>
                          )}
                          {standardFields
                            .filter(field => field.tab === index || (!field.tab && index === 0))
                            .map((field, idx) => {
                              if (
                                field.translationsList &&
                                !isEmptyTranslatedFields &&
                                translatedFields.length > 0
                              ) {
                                return (
                                  <fieldset key={idx}>
                                    <legend className="translationTitle">
                                      {this.props.translation.displayName}
                                    </legend>
                                    {formErrors.translations ? (
                                      <ul className="parsley-errors-list filled">
                                        <li className="parsley-required">
                                          <ErrorTabContainer>
                                            <FormattedMessage {...messages.tabsErrors} />
                                          </ErrorTabContainer>
                                        </li>
                                      </ul>
                                    ) : (
                                      ""
                                    )}
                                    <StyledTranslationTabs
                                      activeKey={this.state.localesTabKey}
                                      onSelect={this.handleSelect.bind(this)}
                                      id="tabID">
                                      {formValues.translations
                                        .filter(field => field.tab === index || index === 0)
                                        .map((translation, idx) =>
                                          renderTranslationTab({
                                            key: translation.locale,
                                            translation,
                                            idx,
                                            handleOnChange,
                                            formErrors,
                                            translatedFields,
                                            entity,
                                            entities,
                                            validation,
                                            renderers,
                                            formValues,
                                            tickers,
                                            relatedEntities,
                                            hasAdvancedTranslations,
                                          })
                                        )}
                                    </StyledTranslationTabs>
                                  </fieldset>
                                );
                              } else {
                                return (
                                  <Field
                                    field={field}
                                    entity={entity}
                                    entities={entities}
                                    renderers={renderers}
                                    formValues={formValues}
                                    onChange={handleOnChange}
                                    validation={validation[field.fieldName]}
                                    errors={formErrors[field.fieldName]}
                                    value={formValues[field.fieldName]}
                                    hasErrors={hasErrors(field.fieldName)}
                                    key={idx}
                                    tickers={tickers}
                                    relatedEntities={relatedEntities}
                                    locale={locale}
                                    updateEntity={this.props.updateEntity}
                                    updateFormValue={this.updateFormValue}
                                    showAdvancedTranslations={showAdvancedTranslations}
                                    locales={locales}
                                    ListWidget={nestedListWidgets[field.fieldName]}
                                    FormWidget={nestedFormWidgets[field.fieldName]}
                                    allowImageEdit={allowImageEdit}
                                  />
                                );
                              }
                            })}

                          {!hasTranslationsList &&
                          !isEmptyTranslatedFields &&
                          translatedFields.some(field => field.tab === index || index === 0) ? (
                            <fieldset>
                              <legend className="translationTitle">
                                {this.props.translation.displayName}
                              </legend>
                              {formErrors.translations ? (
                                <ul className="parsley-errors-list filled">
                                  <li className="parsley-required">Some tabs have errors.</li>
                                </ul>
                              ) : (
                                ""
                              )}
                              <StyledTranslationTabs
                                activeKey={this.state.localesTabKey}
                                onSelect={this.handleSelect.bind(this)}
                                id="tabID">
                                {formValues.translations
                                  .filter(field => field.tab === index || index === 0)
                                  .map((translation, idx) =>
                                    renderTranslationTab({
                                      key: translation.locale,
                                      translation,
                                      idx,
                                      handleOnChange,
                                      formErrors,
                                      translatedFields,
                                      entity,
                                      entities,
                                      validation,
                                      renderers,
                                      formValues,
                                      tickers,
                                      relatedEntities,
                                    })
                                  )}
                              </StyledTranslationTabs>
                            </fieldset>
                          ) : (
                            ""
                          )}
                          {!readOnly && !tab.hideReminderMessage && this.state.dirty && !nested && (
                            <ReminderContainer className="nestedSaveReminder">
                              <FormattedMessage {...messages.remember} />
                            </ReminderContainer>
                          )}
                          {this.state.customValidateError && (
                            <EmptyFields>{this.state.customValidateError}</EmptyFields>
                          )}
                          {entityError && !this.state.dirty && (
                            <EntityErrorMessage entityError={entityError} />
                          )}
                          {entityResult && !this.state.dirty && (
                            <EntityResultMessage entityResult={entityResult} />
                          )}
                          {Object.keys(this.state.formErrors).length >= 1 &&
                            this.state.showEmptyFieldsError && (
                              <EmptyFields>
                                <FormattedMessage {...messages.blankErrors} />
                              </EmptyFields>
                            )}
                          {!readOnly && !tab.hideSubmitButton && submitButton}
                          {!tab.hideBackButton && backButton}
                        </form>
                      </Panel>
                    </Tab>
                  ))}
              </StyledTabs>
            )}
          </MenuContainer>
        </ContentWrapper>
        {!nested && (
          <BackToTopButton
            onClick={this.handleScrollToTop}
            showBackToTopButton={this.state.showBackToTopButton}>
            <i />
          </BackToTopButton>
        )}
      </StyledContentWrapper>
    );
  }
}

EntityForm.propTypes = {
  name: PropTypes.string,
  entity: PropTypes.object,
  validation: PropTypes.object,
  renderers: PropTypes.object,
  fields: PropTypes.array,
  locales: PropTypes.array,
  translation: PropTypes.object,
  onSubmit: PropTypes.func,
  entityToFormValuesMapper: PropTypes.func,
  // Function hook used to transform state before it is sent
  // in onChange Form Function
  beforeFormStateSetHook: PropTypes.func,
  tickers: PropTypes.array,
  resetResult: PropTypes.func,
  updateEntity: PropTypes.func,
};

EntityForm.defaultProps = {
  entityToFormValuesMapper: entityToFormValuesMapper,
  nestedListWidgets: {},
  nestedFormWidgets: {},
  buttonMessage: "submit",
};

export default withUserAuthInfo(EntityForm);
